import type { EmployeeRecordingMode, EmployeeRecordingQuality, EmployeeRecordingRetension } from './Employee'

export interface PbxSettingsRequest {
	/** Id of customer the settings belongs to. */
	_id: string
	/** Id of the default music on hold set to use, null for default. See Music on hold. */
	defaultMusicOnHold?: string
	/** Master pin code for hotdesking, limitations and * codes. 4-10 digits. Not visible for PERSONAL users. */
	masterPinCode?: string
	/** ISO3166-1 2-char default language code, for audio/ui when no employee is associated, e.g. da, en etc. */
	defaultLanguage: string
	/** Default phone number to present on outbound calls, if no a-number was specified on an outbound call. */
	defaultNumber: string
	/** Id of default SIP account to invoice calls to when an outbound call does not present a phone number that belongs to a PBX trunk. */
	defaultVoiceAccount: string
	/** National prefix for the country the hosted PBX belongs to. Used to determine if a call is international and in UI to remove prefix for readability, null if not used. */
	nationalPrefix: string
	/** How to handle caller ID name on outbound calls: */
	callerIdNameOutbound: PbxSettingsCallerIdNameOutbound
	/** Id of dialplan item to redirect to if a caller presses '0' during voicemail greeting or recording. */
	voicemailBreakout?: string
	/** Defaults for common recordings that does not belong to an employee */
	recording: {
		/** When to record calls */
		mode: EmployeeRecordingMode
		/** Quality of call recordings */
		quality: EmployeeRecordingQuality
		/** Number of days to store recording: 1, 3, 7, 14 or 30 days. For COMPACT quality 90, 180, 365 & 730 days are also available. */
		retension: EmployeeRecordingRetension
		/** What employees below MANAGER are allowed to do with their recordings */
		permissions?: PbxSettingsRecordingPermissions
	}
	billing: {
		/** Date until which user and services has been invoiced. */
		invoicedUntil?: Date
	}
	/** True to present mobile number on local calls to mobile phones. */
	presentMobileOnLocalToMobile: boolean
}

export interface PbxSettingsResponse {
	/** Id of customer the settings belongs to. */
	_id: string
	/** Id of the default music on hold set to use, null for default. See Music on hold. */
	defaultMusicOnHold?: string
	/** Master pin code for hotdesking, limitations and * codes. 4-10 digits. Not visible for PERSONAL users. */
	masterPinCode?: string
	/** ISO3166-1 2-char default language code, for audio/ui when no employee is associated, e.g. da, en etc. */
	defaultLanguage: string
	/** Default phone number to present on outbound calls, if no a-number was specified on an outbound call. */
	defaultNumber: string
	/** Id of default SIP account to invoice calls to when an outbound call does not present a phone number that belongs to a PBX trunk. */
	defaultVoiceAccount: string
	/** National prefix for the country the hosted PBX belongs to. Used to determine if a call is international and in UI to remove prefix for readability, null if not used. */
	nationalPrefix: string
	/** How to handle caller ID name on outbound calls: */
	callerIdNameOutbound: PbxSettingsCallerIdNameOutbound
	/** Id of dialplan item to redirect to if a caller presses '0' during voicemail greeting or recording. */
	voicemailBreakout?: string
	/** Defaults for common recordings that does not belong to an employee */
	recording: {
		/** When to record calls */
		mode: EmployeeRecordingMode
		/** Quality of call recordings */
		quality: EmployeeRecordingQuality
		/** Number of days to store recording: 1, 3, 7, 14 or 30 days. For COMPACT quality 90, 180, 365 & 730 days are also available. */
		retension: EmployeeRecordingRetension
		/** What employees below MANAGER are allowed to do with their recordings */
		permissions?: PbxSettingsRecordingPermissions
	}
	billing: {
		/** Date until which user and services has been invoiced. */
		invoicedUntil?: Date
	}
	/** True to present mobile number on local calls to mobile phones. */
	presentMobileOnLocalToMobile: boolean
}

export interface PbxSettingsRecordingPermissions {
	/** True if employees are allowed to transcribe recordings. */
	transcribe: boolean
	/** True if employees are allowed to have recordings summarized by the AI. */
	summarize: boolean
}

export enum PbxSettingsCallerIdNameOutbound {
	/** Send as is */
	AS_IS = 'AS_IS',
	/** Always show customer name (default) */
	CUSTOMER = 'CUSTOMER',
	/** Prefix with customer name if extension has a user, otherwise use customer name */
	PREFIX = 'PREFIX'
}
